import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services/auth.service';
import type { User, Organization } from '@/types/user.types';
import { sounds } from '@/utils/soundEffects';

// WHY ZUSTAND HERE (vs Redux, vs Context):
// Auth/session state is small, read frequently across unrelated parts
// of the tree (route guards, topbar, permission checks), and changes
// rarely. Zustand gives us that global, selector-based access without
// Redux's action/reducer/selector boilerplate, and without the
// re-render-everything cost of plain Context (components subscribe to
// only the slice they select, e.g. `useAuthStore(s => s.user)`).
// Redux Toolkit remains the better call for domains with complex,
// interdependent client state and a need for time-travel debugging —
// this app doesn't have that; almost all "state" here is server state
// owned by React Query instead.
interface AuthState {
  user: User | null;
  currentOrganization: Organization | null;
  isPlatformAdmin: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  status: 'idle' | 'authenticating' | 'authenticated' | 'unauthenticated';
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<string>;
  setUser: (user: User) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      currentOrganization: null,
      isPlatformAdmin: false,
      accessToken: null,
      refreshToken: null,
      status: 'idle',

      login: async (username, password) => {
        set({ status: 'authenticating' });
        const { user, tokens, current_organization, is_platform_admin } = await authService.login(username, password);
        set({
          user,
          currentOrganization: current_organization,
          isPlatformAdmin: is_platform_admin,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          status: 'authenticated',
        });
      },

      logout: () => {
        sounds.playSignOut();
        void authService.logout().catch(() => undefined);
        set({ user: null, currentOrganization: null, isPlatformAdmin: false, accessToken: null, refreshToken: null, status: 'unauthenticated' });
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) throw new Error('No refresh token available');
        const tokens = await authService.refresh(refreshToken);
        set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
        return tokens.accessToken;
      },

      setUser: (user) => set({ user }),

        checkAuth: async () => {
          try {
            const { refreshToken } = get();
            if (!refreshToken) {
              set({ status: 'unauthenticated' });
              return;
            }
            set({ status: 'authenticating' });
            const { user, current_organization, is_platform_admin } = await authService.fetchCurrentUser();
            set({ user, currentOrganization: current_organization, isPlatformAdmin: is_platform_admin, status: 'authenticated' });
          } catch (error) {
            set({ user: null, currentOrganization: null, isPlatformAdmin: false, accessToken: null, refreshToken: null, status: 'unauthenticated' });
          }
        },
    }),
    {
      name: 'edms-auth',
      // Persist tokens so the session survives hard refreshes.
      partialize: (state) => ({ 
        accessToken: state.accessToken,
        refreshToken: state.refreshToken 
      }),
    },
  ),
);

export const selectPermissions = (state: AuthState): string[] => {
  const isSuperAdmin = state.user?.roles.some(r => r.name === 'super-admin');
  if (isSuperAdmin) {
    // If we have a constant for all permissions, we could return them, or just a wildcard
    // Since usePermission checks exact string match, we need to return all known permissions 
    // or modify usePermission to check for 'Super Admin' role directly.
  }
  return state.user?.roles.flatMap((r) => r.permissions?.map((p: any) => p.name || p) || []) ?? [];
};
